<template>
  <HubStage class="auth">
    <header class="auth__head">
      <img class="auth__brand" src="/EasyHUB-lockup.png" alt="easy HUB" />
      <p class="auth__tools">{{ tools }}</p>
    </header>

    <main class="auth__card">
      <nav v-if="showTabs" class="auth__tabs">
        <RouterLink :to="{ name: 'login' }" class="auth__tab" :class="{ 'auth__tab--on': route.name === 'login' }">
          {{ t('layouts.auth.card.content.login') }}
        </RouterLink>
        <RouterLink :to="{ name: 'signup' }" class="auth__tab" :class="{ 'auth__tab--on': route.name === 'signup' }">
          {{ t('layouts.auth.card.content.signUp') }}
        </RouterLink>
      </nav>

      <RouterView />
    </main>
  </HubStage>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import HubStage from '@/components/hub/HubStage.vue'
import { WORKSPACES } from '@/constants/workspaces'

/**
 * A porta do Easy HUB.
 *
 * Mesmo palco da antessala — fundo #08080a, as MESMAS 150 estrelas (ver
 * hubSky.ts), nebulosas e a luz que segue o cursor — para que entrar seja
 * atravessar uma porta, não trocar de cenário.
 *
 * A tela é escura por conta própria: todas as cores estão escritas aqui e nas
 * páginas filhas. Não dá para depender do tema do app, porque o dark dele é um
 * arquivo CSS trocado por <link> que não redefine as variáveis do Vuestic que
 * este layout usava — no escuro o cartão continuava branco.
 */

const route = useRoute()
const { t } = useI18n()

// Só as duas telas que são realmente abas. Em recuperar/redefinir senha as
// abas apareciam sem nenhuma selecionada.
const showTabs = computed(() => route.name === 'login' || route.name === 'signup')

// Vitrine do que existe atrás da porta. Sai da mesma lista que alimenta o
// seletor de áreas, então não envelhece sozinha.
const tools = computed(() => WORKSPACES.map((w) => w.brand).join(' · '))

onMounted(() => {
  // O <link id="app-theme"> nasce sem href e, numa visita a /auth, não há token
  // — então o boot do main.ts nunca chama setAppTheme e NENHUM dos dois CSS
  // globais está carregado. Quem carregava o claro aqui era justamente a trava
  // de tema que esta tela tinha. Trocamos por dark, que é o que a tela é.
  try {
    ;(window as unknown as { setAppTheme?: (t: string) => void }).setAppTheme?.('dark')
  } catch {
    /* tema é best-effort */
  }
})
</script>

<style scoped lang="scss">
$mono: 'JetBrains Mono', ui-monospace, monospace;

.auth {
  &__head {
    display: flex;
    flex-direction: column;
    align-items: center;
    // A arte vem colada na tinta, então o respiro tem que vir daqui.
    gap: 20px;
    margin-bottom: 32px;
  }

  // A arte original do Easy HUB (public/EasyHUB.png), recortada na tinta —
  // o arquivo tem 58% de transparência em volta, e sem o recorte não dá para
  // dimensionar pela altura nem alinhar o respiro até a linha de baixo.
  &__brand {
    height: 92px;
    width: auto;
    display: block;
  }

  &__tools {
    margin: 0;
    font-family: $mono;
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0.22em;
    color: rgba(232, 232, 232, 0.45);
    text-align: center;
    line-height: 1.6;

    // No celular a linha inteira encostava nas duas margens.
    @media (max-width: 420px) {
      font-size: 9px;
      letter-spacing: 0.16em;
    }
  }

  // Mesmo vidro dos cartões da antessala.
  &__card {
    width: 100%;
    max-width: 420px;
    padding: 26px 26px 24px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(22, 22, 26, 0.72);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow:
      0 24px 50px -24px rgba(0, 0, 0, 0.85),
      inset 0 1px 0 rgba(255, 255, 255, 0.09);
  }

  &__tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 22px;
    padding-bottom: 2px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  &__tab {
    flex: 1;
    padding: 9px 4px 11px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: rgba(232, 232, 232, 0.5);
    text-decoration: none;
    border-bottom: 2px solid transparent;
    margin-bottom: -3px;
    transition:
      color 0.2s,
      border-color 0.2s;

    &:hover {
      color: #e8e8e8;
    }

    &--on {
      color: #e8e8e8;
      border-bottom-color: #fa2b00;
    }

    &:focus-visible {
      outline: 2px solid #fa2b00;
      outline-offset: 2px;
      border-radius: 6px;
    }
  }
}
</style>

<style lang="scss">
// ── Vuestic dentro da porta ────────────────────────────────────────────
// Precisa ser global porque alcança as entranhas dos componentes do Vuestic,
// mas tudo está preso a .auth: nada vaza para o resto do app. O layout antigo
// tinha um `color: #000 !important` nos inputs sem esse cuidado, e a regra
// valia em qualquer tela que tivesse a classe no DOM.
.auth {
  .va-input-wrapper__label,
  .va-input-wrapper .va-input-label {
    color: rgba(232, 232, 232, 0.55) !important;
    font-size: 10px;
    letter-spacing: 0.14em;
  }

  .va-input-wrapper__field {
    background: rgba(255, 255, 255, 0.04);
    border-radius: 9px;

    input,
    textarea {
      color: #e8e8e8 !important;

      &::placeholder {
        color: rgba(232, 232, 232, 0.3);
      }
    }
  }

  // O Vuestic desenha a borda do campo num ::before/::after do wrapper.
  .va-input-wrapper__container {
    border-color: rgba(255, 255, 255, 0.14) !important;
  }

  .va-input-wrapper--focused .va-input-wrapper__container {
    border-color: #fa2b00 !important;
  }

  // Autofill do Chrome pinta o campo de azul-claro e devolve texto preto.
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-text-fill-color: #e8e8e8;
    -webkit-box-shadow: 0 0 0 1000px #1b1b20 inset;
    caret-color: #e8e8e8;
  }

  .va-checkbox__label {
    color: rgba(232, 232, 232, 0.75);
    font-size: 13px;
  }

  // Botão da marca: o mesmo laranja-vermelho do HUB, no lugar do gradiente
  // vinho que cada tela repetia em style inline.
  .auth-btn {
    width: 100%;
    height: 40px;
    border-radius: 10px;
    border: none;
    background: #fa2b00;
    color: #fff;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition:
      background 0.2s,
      box-shadow 0.2s,
      transform 0.1s;

    &:hover:not(:disabled) {
      background: #ff4d2e;
      box-shadow: 0 0 24px -6px rgba(250, 43, 0, 0.6);
    }
    &:active:not(:disabled) {
      transform: translateY(1px);
    }
    &:disabled {
      opacity: 0.55;
      cursor: default;
    }
    &:focus-visible {
      outline: 2px solid #ff7a5c;
      outline-offset: 2px;
    }

    // Os "Voltar" são RouterLink, então viram <a> e não herdam a caixa do botão.
    &--link {
      display: flex;
      align-items: center;
      justify-content: center;
      text-decoration: none;
      margin-top: 10px;
    }

    &--ghost {
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.16);
      color: rgba(232, 232, 232, 0.8);

      &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.06);
        box-shadow: none;
        color: #e8e8e8;
      }
    }
  }

  // Giro de carregamento dos botões — os VaButton foram trocados por <button>
  // simples, que é o que dá para tematizar sem brigar com o Vuestic.
  .auth-spin {
    display: inline-block;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.35);
    border-top-color: #fff;
    vertical-align: -2px;
    animation: auth-spin 0.7s linear infinite;
  }

  @keyframes auth-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .auth-link {
    color: rgba(232, 232, 232, 0.6);
    font-size: 12.5px;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #ff5a38;
    }
  }

  .auth-title {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 700;
    color: #e8e8e8;
  }

  .auth-help {
    margin: 0 0 18px;
    font-size: 13px;
    line-height: 1.5;
    color: rgba(232, 232, 232, 0.6);
  }
}
</style>
